import type { CompanyCard, ReplayEvent } from "../packages/db/src/public-types";
import { SECTION_LABELS } from "../packages/ch/src/sic";
import { displayName } from "../packages/db/src/text";
export { displayName };
import { KIND_COLOUR, SECTION_COLOUR } from "./palette";
import { ukDate, ukTime } from "./slots";

export interface Filters { kinds: Set<"birth" | "death">; sections: Set<string>; region: string | null }

export const filters: Filters = { kinds: new Set(["birth", "death"]), sections: new Set(), region: null };

export const matches = (ev: Pick<ReplayEvent, "kind" | "section" | "region">): boolean =>
  filters.kinds.has(ev.kind) &&
  (filters.sections.size === 0 || filters.sections.has(ev.section)) &&
  (filters.region === null || ev.region === filters.region);

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const nf = new Intl.NumberFormat("en-GB");
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);


/* ---------------- counters ---------------- */

export interface Stats {
  date: string; births: number; deaths: number; net: number; births_via_agents: number;
  deaths_via_agents: number; updated_at: number | null; live: boolean;
}

const session = { birth: 0, death: 0 };

export function countReplayed(kind: "birth" | "death"): void {
  session[kind]++;
  if (!live) renderCounts(session.birth, session.death);
}

let live = false;

function renderCounts(b: number, d: number): void {
  $("c-born").textContent = nf.format(b);
  $("c-died").textContent = nf.format(d);
  const net = b - d;
  $("c-net").textContent = `${net > 0 ? "+" : net < 0 ? "−" : ""}${nf.format(Math.abs(net))}`;
}

export function renderStats(s: Stats, replaying: boolean): void {
  live = s.live && !replaying;
  document.body.classList.toggle("is-replay", !live);
  $("c-label").textContent = document.body.classList.contains("og") ? (live ? "Today" : "In the last hour") : live ? "Today" : "Replayed this visit";
  if (live) {
    renderCounts(s.births, s.deaths);
    const agents = s.births_via_agents + s.deaths_via_agents;
    $("c-agents").textContent = agents ? `+ ${nf.format(agents)} registered via formation agents today` : "";
    $("c-updated").textContent = s.updated_at ? `Updated ${ukTime(s.updated_at)}` : "";
  } else {
    renderCounts(session.birth, session.death);
    $("c-agents").textContent = "";
    $("c-updated").textContent = "";
  }
}

/* ---------------- filters ---------------- */

export function buildFilters(onChange: () => void): void {
  const chips = $("chips");
  const all = document.createElement("button");
  all.className = "chip chip-all";
  all.textContent = "All sectors";
  all.setAttribute("aria-pressed", "true");
  chips.append(all);

  const sectionButtons: HTMLButtonElement[] = [];
  for (const [code, label] of Object.entries(SECTION_LABELS)) {
    if (code === "U" || code === "T") continue; // near-empty sections just add clutter
    const b = document.createElement("button");
    b.className = "chip";
    b.dataset.section = code;
    b.style.setProperty("--c", SECTION_COLOUR[code] ?? "#888");
    b.innerHTML = `<i></i>${esc(label)}`;
    b.setAttribute("aria-pressed", "false");
    b.addEventListener("click", () => {
      filters.sections.has(code) ? filters.sections.delete(code) : filters.sections.add(code);
      sync();
    });
    sectionButtons.push(b);
    chips.append(b);
  }
  all.addEventListener("click", () => { filters.sections.clear(); sync(); });

  function sync(): void {
    all.setAttribute("aria-pressed", String(filters.sections.size === 0));
    for (const b of sectionButtons) b.setAttribute("aria-pressed", String(filters.sections.has(b.dataset.section!)));
    onChange();
  }

  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-kind]")) {
    b.addEventListener("click", () => {
      const k = b.dataset.kind as "birth" | "death" | "both";
      filters.kinds = new Set(k === "both" ? ["birth", "death"] : [k]);
      for (const o of document.querySelectorAll<HTMLButtonElement>("[data-kind]")) o.setAttribute("aria-pressed", String(o === b));
      onChange();
    });
  }

  $<HTMLSelectElement>("region").addEventListener("change", (e) => {
    filters.region = (e.target as HTMLSelectElement).value || null;
    onChange();
  });
}

/* ---------------- ticker ---------------- */

const TICKER_MAX = 6;

export function tick(ev: ReplayEvent, onPick: (n: string) => void): void {
  const list = $("ticker");
  const li = document.createElement("li");
  li.className = `t-${ev.kind}`;
  li.innerHTML = `<button><b>${esc(displayName(ev.name))}</b><span>${ev.kind === "birth" ? "Registered in" : "Dissolved ·"} ${esc(ev.town ?? "the UK")}</span></button>`;
  li.querySelector("button")!.addEventListener("click", () => onPick(ev.number));
  list.prepend(li);
  while (list.children.length > TICKER_MAX) list.lastElementChild!.remove();
}

/* ---------------- card / bottom sheet ---------------- */

function yearsBetween(from: string, to: string): number {
  const a = new Date(from), b = new Date(to);
  let y = b.getUTCFullYear() - a.getUTCFullYear();
  if (b.getUTCMonth() < a.getUTCMonth() || (b.getUTCMonth() === a.getUTCMonth() && b.getUTCDate() < a.getUTCDate())) y--;
  return y;
}

export async function openCard(number: string): Promise<void> {
  const card = $("card");
  const body = $("card-body");
  body.innerHTML = `<p class="muted">Loading…</p>`;
  card.hidden = false;
  requestAnimationFrame(() => card.classList.add("open"));
  const res = await fetch(`/api/company/${encodeURIComponent(number)}`);
  if (!res.ok) { body.innerHTML = `<p class="muted">We couldn't load that company just now.</p>`; return; }
  const c = (await res.json()) as CompanyCard;

  const dissolved = c.status.toLowerCase() === "dissolved";
  const today = new Date().toISOString().slice(0, 10);
  const age = c.incorporated_on ? yearsBetween(c.incorporated_on, c.dissolved_on ?? today) : null;
  const ageText = age === null ? "" : age < 1 ? "Under a year" : `${age} year${age === 1 ? "" : "s"}`;
  const section = c.sic_section ?? "X";
  const where = c.via_agent ? "Registered via a formation agent" : `Registered in ${esc(c.town ?? "the UK")}${c.region && c.region !== c.town ? `, ${esc(c.region)}` : ""}`;

  body.innerHTML = `
    <p class="card-kicker" style="--c:${dissolved ? KIND_COLOUR.death : KIND_COLOUR.birth}">
      <i></i>${dissolved ? "Dissolved" : "Active"} · No. ${esc(c.company_number)}
    </p>
    <h2 id="card-title"><a href="/company/${encodeURIComponent(c.company_number)}" title="Open the full company page">${esc(displayName(c.name))}</a></h2>
    <p class="card-where">${where}</p>
    <dl>
      <dt>Incorporated</dt><dd>${ukDate(c.incorporated_on)}</dd>
      ${dissolved ? `<dt>Dissolved</dt><dd>${ukDate(c.dissolved_on)}</dd>` : ""}
      ${ageText ? `<dt>${dissolved ? "Aged" : "Age"}</dt><dd>${ageText}</dd>` : ""}
      <dt>Industry</dt>
      <dd><span class="sec" style="--c:${SECTION_COLOUR[section]}"><i></i>${esc(SECTION_LABELS[section] ?? "Unknown")}</span>
        ${c.sic_description ? `<small>${esc(c.sic1 ?? "")} · ${esc(c.sic_description)}</small>` : ""}</dd>
    </dl>
    <p class="card-links">
      <a class="card-cta" href="/company/${encodeURIComponent(c.company_number)}">Full company page →</a>
      <a class="card-link" href="https://find-and-update.company-information.service.gov.uk/company/${encodeURIComponent(c.company_number)}" target="_blank" rel="noopener noreferrer">Companies House ↗</a>
    </p>`;
}

export function closeCard(): void {
  const card = $("card");
  card.classList.remove("open");
  setTimeout(() => { if (!card.classList.contains("open")) card.hidden = true; }, 250);
}
